import React, { CSSProperties, ElementType, JSXElementConstructor, ReactNode, Ref, forwardRef, useMemo } from 'react';

import { addRootNodeClassName, cn } from '../../lib/css/className/className';
import { Color } from '../../internal/types/designSystemTypes';
import { useInternalVerticalAlignWithSkin } from '../../hooks/useVerticalAlign/useVerticalAlignWithSkin';
import { TypographyRole } from '../../hooks/useTypography/useTypography';
import { useTypographyWithSkin } from '../../hooks/useTypography/useTypographyWithSkin';
import { HStack, Stack, VStack } from '../Stack';
import { HelpProps } from '../Help/types';
import { TestProps } from '../../internal/types/testTypes'; // не нужно сокращать, будет циклическая зависимость
import { StackAlignBase } from '../Stack/types';
import { useTheme } from '../../lib/theme/ThemeProvider';
import { SkinSize } from '../../lib/skin/getSizeSkin/generated/types';
import { getSizeSkin } from '../../lib/skin/getSizeSkin/getSizeSkin';

import { TextPresetWithSkin } from './types';

import './TextGeneric.css';
import './TextGenericWithSkin.css';

/* Оставила класс Текст, не нужно убирать */
const cls = cn('Text');

export type TextAlign = 'left' | 'center' | 'right';

export const textAlignToStack: Record<TextAlign, StackAlignBase> = {
    left: 'flex-start',
    center: 'center',
    right: 'flex-end',
};

export type TextTruncationProps = {
    /**
     * Признак обрезания текста при переполнении контейнера.
     * После обрезки контент занимает одну строку.
     * Для многострочного текста можно использовать свойство `maxLines`
     */
    isTruncated?: boolean;

    /**
     * Максимальное количество строк, доступных к прочтению без обрезания контента
     */
    maxLines?: number;
};

export interface TextProps extends TestProps, TextTruncationProps {
    /**
     * Названия классов для элемента
     */
    className?: string;

    /**
     * Названия классов для лейбла
     */
    labelClassName?: string;

    /**
     * Цветовая палитра для текста и описания.
     * Перечисленные цвета определены дизайн-системой
     * При `current` используется значение `text-main`/`text-supplementary` из текущего цветового скина
     * @default gray
     */
    color?: Color | 'current';

    /**
     * Размер текста из выбранного скина (main, body, caption или их комбинация)
     * Значения с парами предназначены для задания стиля основному тексту и описанию.
     * Перечисленные сочетания размеров определены дизайн-системой
     */
    textSize: TextPresetWithSkin;

    /**
     * Размерный скин, навешанный на родительскую ноду, из которого необходимо получить размеры аддонов
     */
    skinSize: SkinSize;

    /**
     * Основной текст (заголовок или абзац)
     */
    children?: ReactNode;

    /**
     * Дополнительный поясняющий текст (описание к заголовку)
     */
    caption?: ReactNode;

    /**
     * Выравнивание текстового блока по горизонтали.
     * @default 'left'
     */
    textAlign?: TextAlign;

    /**
     * Компонент со всплывающей подсказкой при клике на знак вопроса.
     * При передаче данного параметра рядом с текстом рисуется иконка `Help` соответствующего размера.
     * @param helpProps - параметры для передадчи во всплывающую подсказку (управляют размером, цветом и выравниванием)
     */
    help?: (helpProps: HelpProps) => ReactNode;

    /**
     * Аддон слева от текста (иконка или другой дополнительный элемент).
     * Размер аддона задаётся в соответствии с размером текста и определён дизайн-системой.
     */
    addonLeft?: ReactNode;

    /**
     * Аддон справа от текста (иконка или другой дополнительный элемент).
     * Размер аддона задаётся в соответствии с размером текста и определён дизайн-системой.
     */
    addonRight?: ReactNode;

    /**
     * Роль элемента типографики: основной (main) или дополнительный (supplementary) текст.
     * От роли зависят возвращаемые textClasses для одиночного текстового элемента
     * @default 'main'
     */
    role?: TypographyRole;

    /**
     * Альтернативный тег для компонента (например, 'h1')
     * @default span
     */
    as?: ElementType | JSXElementConstructor<unknown>;

    /**
     * Жирность текста
     */
    bold?: boolean;

    /**
     * Title — текст подсказки при наведении мышки,
     * полезен в случае, если текст виден не полностью.
     * в случае, если значение не определено, текст показывается не полностью и `children` имеет тип string,
     * то будет использовано значение `children`.
     */
    title?: string;

    /**
     * Реф для контейнера текста
     */
    ref?: Ref<HTMLElement>;
}

/**
 * @internal Внутренний компонент для создания текстовых раскладок
 */
export const TextGenericWithSkin = forwardRef<HTMLElement, TextProps>(function TextGeneric(
    {
        className,
        labelClassName,
        color = 'gray',
        textSize,
        skinSize,
        children,
        caption,
        textAlign = 'left',
        help,
        addonLeft,
        addonRight,
        role = 'main',
        isTruncated,
        maxLines,
        bold,
        title,
        'data-testid': dataTestid = 'Text',
        as: ElementTag = 'span',
    },
    ref,
) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- полиморфный тег принимает общие DOM-пропсы
    const ContentElement = ElementTag as React.ComponentType<Record<string, unknown>>;
    const helpSize = useTheme().sizeTokens[skinSize].helpSize;
    const sizeSkinClass = getSizeSkin(skinSize);

    const {
        textClasses: textAlignClasses,
        captionClasses: captionAlignClasses,
        addonClasses: helpAddonAlignClasses,
    } = useInternalVerticalAlignWithSkin({
        textSize,
        addonType: 'help',
    });

    const { addonClasses: iconAddonAlignClasses } = useInternalVerticalAlignWithSkin({
        textSize,
        addonType: 'icon',
    });

    const { textClasses: textTypographyClasses, captionClasses: captionTypographyClasses } = useTypographyWithSkin({
        size: textSize,
        color,
        role,
        bold,
    });

    const hasAddons = Boolean(addonLeft || addonRight);
    const hasCaption = Boolean(caption);

    const lineClamp = Number(maxLines) > 0 ? maxLines : undefined;
    const style = useMemo<CSSProperties>(() => ({ WebkitLineClamp: lineClamp }), [lineClamp]);

    const textTitle = useMemo(() => {
        if (title) return title;

        return (isTruncated || Boolean(lineClamp)) && typeof children === 'string' ? children : undefined;
    }, [title, isTruncated, lineClamp, children]);

    const iconHelp = help?.({ iconContainerClassName: cls('help', [helpAddonAlignClasses]), size: helpSize });
    const hasHelp = help && iconHelp;
    let content = (
        <ContentElement
            data-testid={`${dataTestid}.Content`}
            className={cls('text', { truncated: isTruncated, lineClamped: Boolean(lineClamp) }, [
                textAlignClasses,
                textTypographyClasses,
            ])}
            style={style}
            title={textTitle}
            ref={ref}
        >
            {children}

            {/* создание всплывающей подсказки при использовании Help, когда текст не обрезается */}
            {iconHelp && !isTruncated && !lineClamp && <span className={cls('helpWrapper')}>{iconHelp}</span>}
        </ContentElement>
    );

    if (hasAddons || hasHelp) {
        content = (
            <HStack
                alignItems={textAlignToStack[textAlign]}
                // Гэп до хэлпа
                gap={'current-help'}
                className={cls('addonContainer', { truncated: isTruncated }, [labelClassName])}
            >
                {addonLeft && <Stack className={cls('addonLeft', [iconAddonAlignClasses])}>{addonLeft}</Stack>}
                {content}

                {/* создание всплывающей подсказки при использовании Help, когда текст обрезается */}
                {(isTruncated || Boolean(lineClamp)) && iconHelp}
                {addonRight && <Stack className={cls('addonRight', [iconAddonAlignClasses])}>{addonRight}</Stack>}
            </HStack>
        );
    }

    if (hasCaption) {
        const captionTitle = (isTruncated || Boolean(lineClamp)) && typeof caption === 'string' ? caption : undefined;

        content = (
            <VStack alignItems={textAlignToStack[textAlign]}>
                {content}

                <span
                    className={cls('caption', { truncated: isTruncated, lineClamped: Boolean(lineClamp) }, [
                        captionAlignClasses,
                        captionTypographyClasses,
                    ])}
                    style={style}
                    title={captionTitle}
                    data-testid={`${dataTestid}.Caption`}
                >
                    {caption}
                </span>
            </VStack>
        );
    }

    if (textAlign !== 'left' && !hasCaption) {
        content = <VStack alignItems={textAlignToStack[textAlign]}>{content}</VStack>;
    }

    return addRootNodeClassName(
        content,
        cls({ textAlign, truncated: isTruncated, lineClamped: Boolean(lineClamp) }, [className, sizeSkinClass]),
        { 'data-testid': dataTestid },
    );
});
